import { useEffect, useState } from "react";
import DeltaMathToggle from "../../../shared/DeltaMathToggle";
import { DisplayPerformanceGraphs } from "./ProgressGraph";
import ProgressScatterPlot from "./ProgressScatterPlot";
import DmAlertModal from "../../../student/components/answerForm/DmAlertModal";
import {
  ProgressLocalStorageInterface,
  ProgressByStandardData,
  ProgressData,
  SchoolData,
  SectionData,
  TeacherData,
  ProgressGraphDataInterface,
} from "../ReportTypes";
import { Standard } from "../../../manager/components/standards/common/types";
import { isEqual } from "lodash";
import LoadingSkeloton from "../../utils/LoadingSkeleton";

const DisplayGlobalPerformanceGraph = ({
  selectedCourse,
  stateData,
  setStateData,
  progressByStandardData,
  progressData,
  progressDataLoading,
  skillCodeToStandardMap,
  schoolData,
  sectionData,
  teacherData,
}: {
  selectedCourse: Standard | undefined;
  stateData: ProgressLocalStorageInterface;
  setStateData: React.Dispatch<
    React.SetStateAction<ProgressLocalStorageInterface | null>
  >;
  progressByStandardData: ProgressByStandardData;
  progressData?: ProgressData;
  progressDataLoading?: boolean;
  skillCodeToStandardMap: Record<string, string[]>;
  schoolData: SchoolData[];
  sectionData: SectionData[];
  teacherData: TeacherData[];
}) => {
  const [showFluency, setShowFluency] = useState(true);
  const [graphData, setGraphData] = useState<ProgressGraphDataInterface[]>();
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState<JSX.Element>();
  const [specificSk, setSpecificSk] = useState<string>();

  useEffect(() => {
    if (progressByStandardData) {
      for (const entry in progressByStandardData) {
        if (entry === "data") {
          const uniqueStudents =
            progressByStandardData[entry]["uniqueStudents"];
          const newGraphData: ProgressGraphDataInterface[] = [];
          const standardMap = progressByStandardData[entry]["standardToData"];
          for (const standard in standardMap) {
            const thisNumStudentAssignments =
              standardMap[standard]["numStudentAssignments"];
            if (thisNumStudentAssignments > 0) {
              newGraphData.push({
                label: standard,
                data: {
                  flu: standardMap[standard]["flu"] || 0,
                  perc: standardMap[standard]["perc"] || 0,
                  problemsPerStudent:
                    thisNumStudentAssignments / uniqueStudents || 0,
                },
              });
            }
          }
          setGraphData(newGraphData);
        }
      }
    }
  }, [progressByStandardData]);

  // Create a reverse mapping of skill codes to standards, so that we can handle data point selection
  useEffect(() => {
    if (skillCodeToStandardMap && specificSk) {
      const skillsArr = Object.keys(skillCodeToStandardMap);
      const specificSkills: string[] = [];
      skillsArr.forEach((sk: string) => {
        if (skillCodeToStandardMap[sk].indexOf(specificSk) !== -1) {
          specificSkills.push(sk);
        }
      });

      if (!isEqual(specificSkills, stateData.skillcodes)) {
        setStateData({
          ...stateData,
          skillcodes: specificSkills,
        });
      }
    }
  }, [specificSk, skillCodeToStandardMap]);

  const handleDataPointSelect = (value: any) => {
    if (Array.isArray(value) && value.length > 1) {
      setModalMessage(
        <>
          {value.map((standard: string, index: number) => (
            <button
              key={index}
              className="mb-4 inline-flex w-full justify-center rounded-md border border-transparent bg-dm-blue px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-dm-dark-blue focus:outline-none focus:ring-2 focus:ring-dm-lightest-blue focus:ring-offset-2 sm:text-sm"
              onClick={() => {
                setSpecificSk(standard);
                setShowModal(false);
              }}
            >
              {standard}
            </button>
          ))}
        </>
      );
      setShowModal(true);
    } else {
      setSpecificSk(value[0]);
    }
  };

  const handleChooseSk = (sk: any) => {
    setSpecificSk(sk);
  };

  return (
    <>
      {graphData && graphData.length > 0 && selectedCourse && (
        <>
          <div className="sm:flex sm:flex-row sm:justify-between">
            <h1 className="font-serif text-lg font-bold">
              {" "}
              {selectedCourse.name}
            </h1>
            <div className="mb-2">
              <DeltaMathToggle
                optionA="Fluency"
                optionB="Completion"
                aSelected={showFluency}
                onChangeFn={() => setShowFluency(!showFluency)}
              />
            </div>
          </div>
          <ProgressScatterPlot
            title={`${
              showFluency ? "Fluency" : "Completion"
            } by Standard for Selected ${stateData.scope}`}
            data={graphData}
            yAxisVariable={showFluency ? "flu" : "perc"}
            onClick={handleDataPointSelect}
          />
          <DmAlertModal
            showModal={showModal}
            setShowModal={setShowModal}
            showButton={false}
            headline={"Select a specific standard."}
            message={modalMessage}
            modalAction={handleChooseSk}
          />
          {progressDataLoading && <LoadingSkeloton />}
          {specificSk && progressData && !progressDataLoading && (
            <>
              <h1 className="mt-2 w-full border-t-2 p-2 text-center">
                Selected Standard: <strong>{specificSk}</strong>
              </h1>
              <p className="pb-4 text-center text-sm text-gray-600">
                {getDescriptionByLabel(selectedCourse, specificSk)}
              </p>

              <DisplayPerformanceGraphs
                progressData={progressData}
                stateData={stateData}
                schoolData={schoolData}
                sectionData={sectionData}
                teacherData={teacherData}
                FormattedSelection={() => <div>not shown</div>}
                isGlobal={true}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

const getDescriptionByLabel: any = (standard: any, label: string) => {
  if (standard.module_data) {
    if (standard.label === label) {
      return standard.description;
    }
  } else if (standard.data) {
    return Object.keys(standard.data).map((id: any) =>
      getDescriptionByLabel(standard.data[id], label)
    );
  }
};

export default DisplayGlobalPerformanceGraph;
